import { Controller } from "@hotwired/stimulus"
import { loadPdfjs } from "../lib/pdfjs_loader"
import { pdfDocumentSource } from "../lib/pdf_document_source"
import { acquireDocument, releaseDocument } from "../lib/pdf_document_cache"

export default class extends Controller {
  static targets = [
    "canvas",
    "container",
    "pageInput",
    "pageCurrent",
    "pageTotal",
    "zoomLevel",
    "loading",
    "error"
  ]

  static values = {
    url: String,
    byteSize: Number,
    // Set only where the viewer is torn down and stood back up over the same file — the
    // File Hub, whose chip switch replaces the whole frame. It opts this mount into the
    // shared document cache, so the parsed PDF (and the page and zoom the user left) come
    // back instead of being downloaded again. Empty everywhere else: those viewers load
    // once and destroy on disconnect, exactly as before.
    cacheKey: String,
    page: { type: Number, default: 1 },
    scale: { type: Number, default: 1 },
    minScale: { type: Number, default: 0.25 },
    maxScale: { type: Number, default: 4 }
  }

  connect() {
    this.pdfDoc = null
    this.mounted = true
    this.pageRendering = false
    this.pageNumPending = null
    this.setupKeyboardShortcuts()
    this.setupPagerRequests()
    this.loadPdf()
  }

  disconnect() {
    this.mounted = false
    this.removeKeyboardShortcuts()
    this.removePagerRequests()
    // A cached document is meant to outlive this mount, so it is handed back along with
    // where the user was rather than destroyed — unless the cache has already moved on to
    // another file, which leaves this the document's last owner. A load still in flight has
    // no position to hand over yet.
    if (this.cacheKeyValue) {
      const position = this.pdfDoc ? { page: this.pageValue, scale: this.scaleValue } : {}
      if (!releaseDocument(this.cacheKeyValue, position) && this.pdfDoc) this.pdfDoc.destroy()
    } else if (this.pdfDoc) {
      this.pdfDoc.destroy()
    }
  }

  // This controller owns the current page for BOTH File Hub panels: the left
  // panel's pager asks for a page rather than setting one, and re-renders from
  // the answer below. One writer means the two panels cannot loop against each
  // other.
  setupPagerRequests() {
    this.boundPagerRequest = this.handlePagerRequest.bind(this)
    window.addEventListener("hub-doc-pager:goto", this.boundPagerRequest)
  }

  removePagerRequests() {
    window.removeEventListener("hub-doc-pager:goto", this.boundPagerRequest)
  }

  handlePagerRequest(event) {
    const page = Number(event.detail?.page)
    if (!Number.isInteger(page)) return

    // The unclamped ask rides along on the announcement so a listener can tell a
    // real page change from this viewer clamping a page it does not have.
    this.renderPage(this.clampPage(page), event.detail?.source || "user", page)
  }

  clampPage(page) {
    const lastPage = this.pdfDoc ? this.pdfDoc.numPages : page

    return Math.max(1, Math.min(page, lastPage))
  }

  // Announces the page the viewer now shows. `source` separates a page the user
  // asked for from one moved programmatically, which is how the left panel knows
  // whether to leave the unpaged pseudo-page it is parked on. `requested` is the
  // page that was asked for, which differs from `page` only when this viewer
  // clamped it — the left panel's page range can legitimately run past the PDF's.
  announcePage(source, requested) {
    window.dispatchEvent(new CustomEvent("pdf-player:page-changed", {
      detail: {
        page: this.pageValue,
        requested: requested,
        numPages: this.pdfDoc ? this.pdfDoc.numPages : null,
        source: source
      }
    }))
  }

  setupKeyboardShortcuts() {
    this.boundKeyHandler = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.boundKeyHandler)
  }

  removeKeyboardShortcuts() {
    document.removeEventListener("keydown", this.boundKeyHandler)
  }

  handleKeydown(event) {
    // Ignore the shortcut while the user is typing in any editable field, so
    // arrow keys move the caret (and +/-/= type literally) instead of paging or
    // zooming the PDF. Covers inputs, textareas, selects, and contenteditable
    // regions like the File Hub rich text block editor (a <div>, not an <input>).
    if (this.isEditableTarget(event.target)) return

    switch (event.key) {
      case "ArrowLeft":
        event.preventDefault()
        this.prevPage()
        break
      case "ArrowRight":
        event.preventDefault()
        this.nextPage()
        break
      case "+":
      case "=":
        event.preventDefault()
        this.zoomIn()
        break
      case "-":
        event.preventDefault()
        this.zoomOut()
        break
    }
  }

  // True when the key event originated in an editable field, so the global PDF
  // shortcut should stand aside. `isContentEditable` covers the rich text editor
  // and any nested element inside a contenteditable region.
  isEditableTarget(target) {
    if (!target) return false
    if (target.isContentEditable) return true

    return ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)
  }

  async loadPdf() {
    if (!this.urlValue) return

    this.showLoading()

    try {
      const source = this.cacheKeyValue
        ? acquireDocument(this.cacheKeyValue, () => this.fetchDocument())
        : { documentPromise: this.fetchDocument(), page: null, scale: null }

      this.pdfDoc = await source.documentPromise

      // Another chip switch replaced this viewer while the load was in flight; its canvas
      // is no longer in the document. A cached document waits for the next mount, but an
      // uncached one has no other owner and would leak.
      if (!this.mounted) {
        if (!this.cacheKeyValue) this.pdfDoc.destroy()
        this.pdfDoc = null
        return
      }

      if (this.hasPageTotalTarget) {
        this.pageTotalTarget.textContent = this.pdfDoc.numPages
      }

      if (source.scale) {
        this.scaleValue = source.scale
        this.updateZoomDisplay()
      } else {
        await this.calculateFitWidth()
      }

      this.hideLoading()
      // "restore" marks a page the user had already chosen before the viewer was torn
      // down, so the File Hub's left panel follows it back instead of sitting on page one.
      this.renderPage(this.clampPage(source.page || this.pageValue), source.page ? "restore" : "load")
    } catch (error) {
      console.error("Error loading PDF:", error)
      this.showError("Failed to load PDF document")
    }
  }

  async fetchDocument() {
    const pdfjsLib = await loadPdfjs()

    return pdfjsLib.getDocument(pdfDocumentSource(this.urlValue, this.byteSizeValue)).promise
  }

  async calculateFitWidth() {
    if (!this.pdfDoc) return

    const page = await this.pdfDoc.getPage(1)
    const viewport = page.getViewport({ scale: 1 })
    this.scaleValue = this.containerContentWidth() / viewport.width
    this.updateZoomDisplay()
  }

  // The container's inner width/height (clientWidth/Height minus its own
  // padding), so "fit" fills the area exactly regardless of how it's padded —
  // the File Hub area has no padding, the purchases card has 24px.
  containerContentWidth() {
    const styles = getComputedStyle(this.containerTarget)
    const paddingX = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight)
    return this.containerTarget.clientWidth - paddingX
  }

  containerContentHeight() {
    const styles = getComputedStyle(this.containerTarget)
    const paddingY = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom)
    return this.containerTarget.clientHeight - paddingY
  }

  async renderPage(pageNum, source = "sync", requested = pageNum) {
    if (!this.pdfDoc) return

    if (this.pageRendering) {
      this.pageNumPending = pageNum
      this.pageNumPendingSource = source
      this.pageNumPendingRequested = requested
      return
    }

    this.pageRendering = true
    this.pageValue = pageNum
    this.announcePage(source, requested)

    try {
      const page = await this.pdfDoc.getPage(pageNum)
      const viewport = page.getViewport({ scale: this.scaleValue })

      const canvas = this.canvasTarget
      const context = canvas.getContext("2d")

      // Set canvas dimensions for high DPI displays
      const outputScale = window.devicePixelRatio || 1
      canvas.width = Math.floor(viewport.width * outputScale)
      canvas.height = Math.floor(viewport.height * outputScale)
      canvas.style.width = Math.floor(viewport.width) + "px"
      canvas.style.height = Math.floor(viewport.height) + "px"

      const transform = outputScale !== 1
        ? [outputScale, 0, 0, outputScale, 0, 0]
        : null

      const renderContext = {
        canvasContext: context,
        transform: transform,
        viewport: viewport
      }

      await page.render(renderContext).promise

      this.pageRendering = false
      this.updatePageInput()

      if (this.pageNumPending !== null) {
        const pendingPage = this.pageNumPending
        const pendingSource = this.pageNumPendingSource || "sync"
        const pendingRequested = this.pageNumPendingRequested
        this.pageNumPending = null
        this.pageNumPendingSource = null
        this.pageNumPendingRequested = null
        this.renderPage(pendingPage, pendingSource, pendingRequested ?? pendingPage)
      }
    } catch (error) {
      console.error("Error rendering page:", error)
      this.pageRendering = false
    }
  }

  // Page Navigation
  prevPage() {
    if (this.pageValue <= 1) return
    this.renderPage(this.pageValue - 1, "user")
  }

  nextPage() {
    if (!this.pdfDoc || this.pageValue >= this.pdfDoc.numPages) return
    this.renderPage(this.pageValue + 1, "user")
  }

  goToPage(event) {
    const pageNum = parseInt(event.target.value, 10)
    if (isNaN(pageNum) || !this.pdfDoc) return

    this.renderPage(this.clampPage(pageNum), "user")
  }

  updatePageInput() {
    if (this.hasPageInputTarget) {
      this.pageInputTarget.value = this.pageValue
    }
    // The File Hub toolbar shows the page as text ("Page X of N") instead of an input.
    if (this.hasPageCurrentTarget) {
      this.pageCurrentTarget.textContent = this.pageValue
    }
  }

  // Zoom Controls
  zoomIn() {
    const newScale = Math.min(this.scaleValue * 1.25, this.maxScaleValue)
    this.setScale(newScale)
  }

  zoomOut() {
    const newScale = Math.max(this.scaleValue / 1.25, this.minScaleValue)
    this.setScale(newScale)
  }

  fitWidth() {
    this.calculateFitWidth().then(() => {
      this.renderPage(this.pageValue)
    })
  }

  fitPage() {
    if (!this.pdfDoc) return

    this.pdfDoc.getPage(this.pageValue).then((page) => {
      const viewport = page.getViewport({ scale: 1 })
      const scaleX = this.containerContentWidth() / viewport.width
      const scaleY = this.containerContentHeight() / viewport.height

      this.setScale(Math.min(scaleX, scaleY))
    })
  }

  setScale(scale) {
    this.scaleValue = scale
    this.updateZoomDisplay()
    this.renderPage(this.pageValue)
  }

  updateZoomDisplay() {
    if (this.hasZoomLevelTarget) {
      this.zoomLevelTarget.textContent = Math.round(this.scaleValue * 100) + "%"
    }
  }

  // Loading/Error States
  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("visible")
    }
    if (this.hasErrorTarget) {
      this.errorTarget.classList.remove("visible")
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("visible")
    }
  }

  showError(message) {
    this.hideLoading()
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.classList.add("visible")
    }
  }
}
